/**
 * MER-585: parsing and resolution for operator-entered rates.
 *
 * Zay's Disposal Tracker accepts "5" or "5%" in the same box and infers the
 * unit from the string. That inference is exactly the shape of defect MER-543
 * was: a unit that is implied rather than carried. Here the string is parsed
 * ONCE, at entry, into an explicit {value, unit} pair that is what gets stored.
 * Nothing downstream re-guesses.
 *
 * His semantics, preserved:
 *   disposal rate      flat $/ton, base 0
 *   env / fsc / other  flat $, OR a percentage OF THE DISPOSAL SUBTOTAL
 *                      (rate x tons) -- not of the row's imported cost
 *
 * Plain .mjs so node:test imports it with no build step.
 */

/** The four columns Zay maintains, in his order. */
export const DISPOSAL_RATE_KEYS = Object.freeze([
  { key: "disposal_rate", label: "Disposal Rate", unit: "usd_per_ton" },
  { key: "environmental_fee", label: "Environmental Fee", unit: "usd" },
  { key: "fsc_fee", label: "FSC Fee", unit: "usd" },
  { key: "other_fee", label: "Other", unit: "usd" },
]);

/**
 * Parse an operator's entry into an explicit value + unit.
 *
 * @param {string|number|null|undefined} raw
 * @param {string} defaultUnit  unit to use when the entry carries no '%'
 * @returns {{value: number, unit: string} | null} null for blank/unparseable,
 *   so a cleared box stores nothing rather than a zero that would read as
 *   "this facility is free".
 */
export function parseRateEntry(raw, defaultUnit = "usd") {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text) return null;

  const isPct = text.endsWith("%");
  // Strip currency, commas and the trailing %, then parse. Commas matter:
  // MER-549 was four months of money silently zeroed by a bare parse.
  const cleaned = text.replace(/%$/, "").replace(/[$,\s]/g, "");
  if (!/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;

  return { value, unit: isPct ? "pct" : defaultUnit };
}

/** Render a stored {value, unit} back into the box the operator typed into. */
export function formatRateEntry(entry) {
  if (!entry || !Number.isFinite(entry.value)) return "";
  return entry.unit === "pct" ? `${entry.value}%` : String(entry.value);
}

/**
 * What one press of Save does with the boxes that were edited (2026-10-01).
 *
 * The client's page stores each box's text and reads a blank and a 0 the same
 * way: manualVal returns 0 for both, and applyManual leaves a location alone
 * when its four values are all 0. Blanking a box and saving is how a rate is
 * taken off on his page. So here:
 *
 *   blank, or 0, over a saved value   CLEAR the saved value
 *   blank, or 0, with nothing saved   nothing (a 0 is never stored)
 *   a number that differs             WRITE it, unit parsed once (parseRateEntry)
 *   the saved number again            nothing
 *   text that is not a number         left as it was, and reported (invalid)
 *
 * @param {Record<string, Record<string, string>>} draft  {location: {rate_key: text}}
 * @param {(location: string, rateKey: string) => {value: number, unit: string} | null | undefined} savedFor
 * @returns {{writes: Array<object>, clears: Array<object>, invalid: Array<object>}}
 *   writes in rateInputsClient.RateWrite shape, clears and invalid as
 *   {scope, scopeKey, rateKey}; facility scope, the four DISPOSAL_RATE_KEYS only.
 */
export function planRateSave(draft, savedFor) {
  const writes = [];
  const clears = [];
  const invalid = [];
  for (const [loc, cols] of Object.entries(draft || {})) {
    if (!loc) continue;
    for (const [key, raw] of Object.entries(cols || {})) {
      const col = DISPOSAL_RATE_KEYS.find((c) => c.key === key);
      if (!col) continue;
      const target = { scope: "facility", scopeKey: loc, rateKey: key };
      const text = String(raw ?? "").trim();
      const parsed = text ? parseRateEntry(text, col.unit) : null;
      if (text && !parsed) {
        invalid.push(target);
        continue;
      }
      const prev = savedFor ? savedFor(loc, key) : null;
      if (!parsed || parsed.value === 0) {
        if (prev) clears.push(target);
        continue;
      }
      if (prev && prev.value === parsed.value && prev.unit === parsed.unit) continue;
      writes.push({ ...target, value: parsed.value, unit: parsed.unit });
    }
  }
  return { writes, clears, invalid };
}

const RATE_NOUN = Object.freeze({ one: "rate", many: "rates" });

/**
 * The status line after a Save or a Reset: what was written and cleared, and
 * what was not. "Saved 2 rates, cleared 1"; "Cleared 1 rate"; "No changes to
 * save" when nothing differed from what is stored.
 *
 * `noun` names what was saved where it is not a rate (Settings > Inputs says
 * "Saved 2 values", "Cleared 1 goal"); the wording is otherwise the same.
 *
 * @param {{saved?: number, cleared?: number, notCleared?: number, invalid?: number, error?: string | null}} [r]
 * @param {{one: string, many: string}} [noun]
 */
export function rateSaveStatus(r = {}, noun = RATE_NOUN) {
  const { saved = 0, cleared = 0, notCleared = 0, invalid = 0, error = null } = r;
  const rates = (count) => `${count} ${count === 1 ? noun.one : noun.many}`;
  const out = [];
  if (saved && cleared) out.push(`Saved ${rates(saved)}, cleared ${cleared}`);
  else if (saved) out.push(`Saved ${rates(saved)}`);
  else if (cleared) out.push(`Cleared ${rates(cleared)}`);
  if (notCleared) out.push(`Could not clear ${rates(notCleared)}: ${error || "unknown error"}`);
  if (!out.length) out.push("No changes to save");
  if (invalid) {
    out.push(
      invalid === 1
        ? "1 entry is not a number and was not saved"
        : `${invalid} entries are not numbers and were not saved`,
    );
  }
  return out.join(". ");
}

/**
 * Collapse rows to the latest entry per (scope, scope_key, rate_key).
 *
 * An edit is a new row, so a facility with three edits has three rows. The
 * newest wins; ties break on nothing, because created_at is a timestamptz
 * default now() and cannot collide meaningfully. A cleared rate has no rows
 * at all (migration 0100 removes them and keeps them in report_edit_log), so
 * it is simply absent here.
 *
 * @param {Array<object>} rows
 * @returns {Map<string, object>} keyed by `${scope}|${scope_key}|${rate_key}`
 */
export function latestRates(rows) {
  const out = new Map();
  for (const r of rows || []) {
    if (!r || !r.rate_key) continue;
    const key = `${r.scope || "facility"}|${r.scope_key || ""}|${r.rate_key}`;
    const prev = out.get(key);
    if (prev && String(r.created_at || "") <= String(prev.created_at || "")) continue;
    out.set(key, r);
  }
  return out;
}

/** Look up one facility's entry for one rate key. */
export function rateFor(latest, scopeKey, rateKey) {
  return latest.get(`facility|${scopeKey}|${rateKey}`) ?? null;
}

/**
 * Zay's applyManual, with the unit carried instead of inferred.
 *
 * Returns null when the facility has no usable entry, so the caller keeps the
 * measured cost rather than substituting a zero. Deliberately a PURE function
 * that returns a figure: nothing in this PR wires it into a KPI, because
 * whether an entered rate should overwrite a measured one is AJ's call, not a
 * code decision. Everything shipped today has run the other way -- never
 * overwrite a measured number with an assumption without saying so.
 *
 * @param {{disposal_rate?: {value:number,unit:string}, environmental_fee?: object, fsc_fee?: object, other_fee?: object}} entries
 * @param {number} tons
 * @returns {{rate: number, cost: number} | null}
 */
export function contractedCost(entries, tons) {
  const e = entries || {};
  const t = Number.isFinite(tons) ? tons : 0;
  const disp = e.disposal_rate && e.disposal_rate.unit !== "pct" ? e.disposal_rate.value : 0;
  const subtotal = disp * t;

  const addOn = (entry) => {
    if (!entry || !Number.isFinite(entry.value)) return 0;
    // A percentage is of the disposal subtotal, per Zay's manualVal(x, disp*tons).
    return entry.unit === "pct" ? (subtotal * entry.value) / 100 : entry.value;
  };

  const env = addOn(e.environmental_fee);
  const fsc = addOn(e.fsc_fee);
  const other = addOn(e.other_fee);
  if (!(disp || env || fsc || other)) return null;
  return { rate: disp, cost: subtotal + env + fsc + other };
}
